<template>
  <article class="video-player" :class="{ 'fullscreen-enabled': fullscreen }">
    <video
      ref="video"
      class="video-element"
      :preload="useNativeControls"
      :controls="useNativeControls"
      @click="togglePlay"
      @loadeddata="onLoadedData"
      @timeupdate="setCurrentTime"
      @progress="updateTimeRangeBuffer"
    >
      <source :src="videoSource" :type="`video/${extension}`" />
    </video>

    <div
      class="video-player-center-icon-holder"
      :hidden="!displayPlayCenterButton"
    >
      <FaIcon
        class="video-player-center-icon"
        :icon="videoPlaying ? 'play-circle' : 'pause-circle'"
        @click="togglePlay"
      />
    </div>

    <div class="video-player-controls" v-if="!useNativeControls">
      <MediaProgressTimeSlider
        ref="progressTimeSlider"
        :duration="videoDuration"
        :current-time="currentTime"
        :time-range-pieces="timeRangePieces"
        @onTimeSliderChange="onTimeSliderChange"
      />

      <div class="video-player-controls-bottom">
        <button
          class="video-player-button"
          :class="videoPlaying ? 'pause' : 'play'"
          type="button"
          :title="
            videoPlaying ? $t('media.buttons.pause') : $t('media.buttons.play')
          "
          :disabled="!videoLoaded"
          @click="togglePlay"
        >
          <FaIcon
            class="video-player-button-icon"
            :icon="videoPlaying ? 'pause' : 'play'"
          />
        </button>

        <button
          class="video-player-button rewind-seconds"
          type="button"
          :title="
            $t('media.buttons.rewind-seconds', {
              seconds: this.skipTime,
            })
          "
          :disabled="!canRewind"
          @click="rewindSeconds()"
        >
          {{ skipTime }}
          <FaIcon class="video-player-button-icon" icon="undo" />
        </button>

        <button
          class="video-player-button skip-seconds"
          type="button"
          :title="
            $t('media.buttons.skip-seconds', {
              seconds: this.skipTime,
            })
          "
          :disabled="!canSkip"
          @click="skipSeconds()"
        >
          <FaIcon class="video-player-button-icon" icon="redo" />
          {{ skipTime }}
        </button>

        <span class="video-player-time-progress-text timeDisplay"
          >{{ remainingTimeDisplay }} / {{ totalTimeDisplay }}</span
        >
        <button
          class="video-player-button mute-sound"
          :title="
            this.muted ? $t('media.buttons.unmute') : $t('media.buttons.mute')
          "
          @click="toggleMuteSound"
        >
          <FaIcon
            class="video-player-mute"
            :icon="muted ? 'volume-slash' : 'volume'"
          />
        </button>
        <button
          v-if="fullscreenAvailable"
          class="video-player-button fullscreen"
          :title="
            this.fullscreen
              ? $t('media.buttons.exit-fullscreen')
              : $t('media.buttons.enter-fullscreen')
          "
          @click="toggleFullscreen"
        >
          <FaIcon
            class="video-player-fullscreen"
            :icon="fullscreen ? 'compress' : 'expand'"
          />
        </button>
      </div>
    </div>
  </article>
</template>

<script>
import FaIcon from '../ui/FaIcon'
import MediaProgressTimeSlider from './MediaProgressTimeSlider'

import logger from '@/utils/logger'
import { getVideoUrl } from '@/utils/network'
import { formatDuration, SECOND } from '@/utils/date'
import { isOnMobileDevice } from '@/utils/device'

/**
 * Please note that either a video ID OR a src (full URL without extension) should be provided for the player to function.
 */
export default {
  name: 'VideoPlayer',
  components: { FaIcon, MediaProgressTimeSlider },
  props: {
    videoId: {
      type: [String, Number],
    },
    src: {
      type: String,
    },
    initialCurrentTime: {
      type: Number,
    },
  },

  data() {
    return {
      fullscreenAvailable: document.fullscreenEnabled,
      fullscreen: false,
      videoLoaded: false,
      videoPlaying: false,
      noMoreVideo: false,
      extension: null,
      skipTime: 10,
      currentTime: 0,
      videoDuration: 0,
      displayPlayCenterButton: false,
      displayPlayCenterButtonTimeout: null,
      timeRangePieces: [],
      useNativeControls: isOnMobileDevice(),
      muted: false,
    }
  },

  created() {
    if (!this.videoId && !this.src) {
      logger.error('Missing video-id or src property')
    } else if (this.videoId && this.src) {
      logger.warn(
        'Both a video-id and src property is present. The video from the src property will be loaded.'
      )
    }
  },

  mounted() {
    if (
      this.$refs.video.canPlayType('video/webm; codecs="vp8, vorbis"') ===
      'probably'
    ) {
      this.extension = 'webm'
    } else {
      this.extension = 'mp4'
    }

    if (this.initialCurrentTime) {
      this.$refs.video.currentTime = this.currentTime = this.initialCurrentTime
    }
  },
  beforeDestroy() {
    clearTimeout(this.displayPlayCenterButtonTimeout)
  },
  watch: {
    videoPlaying() {
      clearTimeout(this.displayPlayCenterButtonTimeout)
      this.displayPlayCenterButton = true
      this.displayPlayCenterButtonTimeout = setTimeout(() => {
        this.displayPlayCenterButton = false
      }, 0.5 * SECOND)
    },
  },
  computed: {
    videoSource() {
      if (this.extension) {
        if (this.src) {
          return `${this.src}.${this.extension}`
        } else if (this.videoId) {
          return getVideoUrl(this.videoId, this.extension)
        }
      }

      return null
    },
    canRewind() {
      return parseInt(this.currentTime) > 0
    },
    canSkip() {
      return (
        Math.floor(this.currentTime) <
        Math.floor(this.videoDuration) - this.skipTime
      )
    },
    remainingTimeDisplay() {
      return formatDuration(this.currentTime * SECOND, true)
    },
    totalTimeDisplay() {
      return formatDuration(this.videoDuration * SECOND, true)
    },
  },
  methods: {
    updateTimeRangeBuffer() {
      if (this.$refs.video) {
        let timeRanges = this.$refs.video.buffered
        let timeRangeLength = timeRanges['length']

        if (timeRanges['length']) {
          this.timeRangePieces.length = 0
          for (let i = 0; i < timeRangeLength; i++) {
            this.timeRangePieces.push({
              start: this.$refs.video.buffered.start(i),
              end: this.$refs.video.buffered.end(i),
            })
          }
        }
      }
    },
    onTimeSliderChange(event) {
      this.$refs.video.currentTime = this.currentTime =
        event.target.valueAsNumber
    },
    skipSeconds() {
      if (this.currentTime + this.skipTime > this.videoDuration) {
        this.$refs.video.currentTime = this.videoDuration
      } else {
        this.$refs.video.currentTime =
          this.$refs.video.currentTime + this.skipTime
      }
    },
    rewindSeconds() {
      if (this.currentTime - this.skipTime < 0) {
        this.$refs.video.currentTime = 0
      } else {
        this.$refs.video.currentTime =
          this.$refs.video.currentTime - this.skipTime
      }
    },
    setCurrentTime(ev) {
      this.currentTime = parseFloat(ev.target.currentTime)

      if (this.$refs.progressTimeSlider) {
        this.$refs.progressTimeSlider.setInternalCurrentTime(this.currentTime)
      }
    },
    togglePlay() {
      if (this.$refs.video && this.$refs.video.paused) {
        this.playVideo()
      } else {
        this.pauseVideo()
      }
    },
    pauseVideo() {
      this.videoPlaying = false
      if (this.$refs.video) {
        this.$refs.video.pause()
      }
    },
    playVideo() {
      if (this.$refs.video) {
        this.videoPlaying = true
        this.$refs.video.play()
      }
    },
    toggleFullscreen() {
      if (document.fullscreenElement) {
        document
          .exitFullscreen()
          .then(() => {
            this.fullscreen = false
          })
          .catch(error => {
            logger.error('Exit fullscreen error', error)
          })
      } else {
        this.$el
          .requestFullscreen()
          .then(() => {
            this.fullscreen = true
          })
          .catch(error => {
            logger.error('Enter fullscreen error', error)
            this.fullscreen = false
          })
      }
    },
    toggleMuteSound() {
      this.$refs.video.muted = this.muted = !this.$refs.video.muted
    },

    onLoadedData() {
      this.videoDuration = this.$refs.video.duration
      this.videoLoaded = true
    },
  },
}
</script>

<style lang="scss" scoped>
.video-player {
  position: relative;
  width: 100%;
  max-width: 100%;
  margin: auto;
  background: var(--gray-light);

  &.fullscreen-enabled {
    background: var(--off-black);
  }

  @media (orientation: landscape) {
    // A width of 120vh makes sure the video height never exceeds the height of the viewport
    width: min(120vh, 100%);
  }

  &:before {
    content: '';
    display: block;
    padding-bottom: 9/16 * 100%; // Sets the aspect ratio of the video player to 16:9
  }
}

.video-element {
  position: absolute;
  display: block;
  top: 0;
  left: 0;
  margin: auto;
  width: 100%;
  height: 100%;
  max-width: none;
  line-height: 0;
}

.video-player-center-icon-holder {
  position: absolute;
  display: inline-block;
  left: 50%;
  top: calc(50% - 0.125em);
  opacity: 1;
  z-index: 1;
  transition: all 0.5s ease-in-out;
  transform: translate(-50%, -50%);

  &[hidden] {
    display: inline-block !important;
    opacity: 0;
    pointer-events: none;
  }

  .video-player-center-icon {
    font-size: 4em;
    color: var(--gray-dark);
  }
}

.video-player-controls {
  --video-player-gradient-dark: rgba(0, 30, 50, 0.5);
  --video-player-gradient-light: rgba(0, 30, 50, 0);

  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0 0.5em;
  z-index: 1;
  background: linear-gradient(
    to bottom,
    var(--video-player-gradient-light, rgba(0, 30, 50, 0)),
    var(--video-player-gradient-dark, rgba(0, 30, 50, 0.5))
  );
}

.video-player-controls-bottom {
  display: flex;
  align-items: baseline;
  white-space: nowrap;

  @media (max-width: 30rem) {
    font-size: 0.8em;
  }
}

.video-player-button,
.video-player-time-progress-text {
  padding: 0.25em 1em;
  background: transparent;
  border: none;
  color: $grayLighter !important;
}

.video-player-button {
  text-overflow: clip;

  &:disabled {
    opacity: 0.5;
  }

  &.fullscreen {
    margin-left: auto;
  }
}
</style>
